import { reactive } from 'vue'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import api, { getAccessToken } from '@/api/client'
import { uploadErrorMessage } from '@/services/uploadErrors'

export type UploadStage =
  | 'sending'
  | 'preparing'
  | 'uploading'
  | 'scanning'
  | 'importing'
  | 'converting'
  | 'compressing'
  | 'optimizing'
  | 'saving'
  | 'finalizing'
  | 'done'
  | 'failed'
  | 'canceled'

export interface ActiveUpload {
  id: string
  kind: string
  stage: UploadStage
  status: string
  networkProgress: number | null
  processingProgress: number | null
  error: string
}

interface UploadSessionPayload {
  id: string
  kind: string
  stage: UploadStage
  status: string
  error?: string
  progress?: number
}

const activeUploads = reactive<ActiveUpload[]>([])
const requestControllers = new Map<string, AbortController>()
const observerControllers = new Map<string, AbortController>()

function uploadById(id: string): ActiveUpload | undefined {
  return activeUploads.find(upload => upload.id === id)
}

function applySession(payload: UploadSessionPayload): void {
  const upload = uploadById(payload.id)
  if (!upload) return
  upload.stage = payload.stage
  upload.status = payload.status
  upload.error = payload.error || ''
  upload.processingProgress = Number.isFinite(payload.progress) && payload.progress! > 0 && payload.progress! < 100
    ? Math.round(payload.progress!)
    : null
  if (payload.stage !== 'sending') upload.networkProgress = null
}

function finishLater(id: string, delay = 6_000): void {
  window.setTimeout(() => {
    const index = activeUploads.findIndex(upload => upload.id === id)
    if (index >= 0) activeUploads.splice(index, 1)
    requestControllers.delete(id)
    observerControllers.get(id)?.abort()
    observerControllers.delete(id)
  }, delay)
}

async function pollUpload(id: string, observerSignal: AbortSignal): Promise<void> {
  while (!observerSignal.aborted) {
    try {
      const { data } = await api.get<UploadSessionPayload>(`/uploads/sessions/${id}`)
      applySession(data)
      if (['done', 'failed', 'canceled'].includes(data.stage)) return
    } catch {
      // A reconnect or token refresh can transiently interrupt polling.
    }
    await new Promise(resolve => window.setTimeout(resolve, 500))
  }
}

async function observeUpload(id: string, controller: AbortController): Promise<void> {
  const token = getAccessToken()
  try {
    const base = String(api.defaults.baseURL || '/api').replace(/\/$/, '')
    const response = await fetch(`${base}/uploads/sessions/${encodeURIComponent(id)}/events`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      signal: controller.signal,
    })
    if (!response.ok || !response.body) throw new Error('SSE unavailable')
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (!controller.signal.aborted) {
      const { done, value } = await reader.read()
      if (done) return
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split(/\r?\n\r?\n/)
      buffer = events.pop() || ''
      for (const event of events) {
        const line = event.split(/\r?\n/).find(item => item.startsWith('data:'))
        if (!line) continue
        applySession(JSON.parse(line.slice(5).trim()) as UploadSessionPayload)
      }
    }
  } catch {
    if (!controller.signal.aborted) await pollUpload(id, controller.signal)
  }
}

export async function trackedUpload<T = unknown>(kind: string, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
  const { data: session } = await api.post<UploadSessionPayload>('/uploads/sessions', { kind })
  const requestController = new AbortController()
  const observerController = new AbortController()
  requestControllers.set(session.id, requestController)
  observerControllers.set(session.id, observerController)
  activeUploads.push({
    id: session.id,
    kind,
    stage: 'sending',
    status: 'running',
    networkProgress: 0,
    processingProgress: null,
    error: '',
  })
  if (config.signal) {
    if (config.signal.aborted) requestController.abort()
    else config.signal.addEventListener?.('abort', () => requestController.abort(), { once: true })
  }
  void observeUpload(session.id, observerController)

  try {
    const response = await api.request<T>({
      ...config,
      timeout: 0,
      signal: requestController.signal,
      headers: { ...config.headers, 'X-Upload-Session': session.id },
      onUploadProgress: event => {
        const upload = uploadById(session.id)
        if (upload && event.total) {
          upload.stage = 'sending'
          upload.networkProgress = Math.min(100, Math.round((event.loaded * 100) / event.total))
          upload.processingProgress = null
        }
        config.onUploadProgress?.(event)
      },
    })
    const upload = uploadById(session.id)
    if (upload) {
      upload.networkProgress = null
      upload.processingProgress = null
      upload.stage = 'done'
      upload.status = 'complete'
    }
    finishLater(session.id, 6_000)
    return response
  } catch (error: any) {
    const upload = uploadById(session.id)
    if (upload) {
      upload.networkProgress = null
      upload.processingProgress = null
      upload.stage = requestController.signal.aborted ? 'canceled' : 'failed'
      upload.status = upload.stage
      upload.error = upload.stage === 'canceled'
        ? 'Upload canceled'
        : uploadErrorMessage(error, 'Upload failed')
    }
    finishLater(session.id, upload?.stage === 'failed' ? 12_000 : 6_000)
    throw error
  }
}

export async function cancelTrackedUpload(id: string): Promise<void> {
  requestControllers.get(id)?.abort()
  try { await api.delete(`/uploads/sessions/${encodeURIComponent(id)}`) } catch { /* request may already be gone */ }
  const upload = uploadById(id)
  if (upload) {
    upload.stage = 'canceled'
    upload.status = 'canceled'
    upload.networkProgress = null
    upload.processingProgress = null
  }
}

export function useTrackedUploads() {
  return { activeUploads, cancelTrackedUpload }
}
